import { useState, memo } from 'react'
import { Copy, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import { AnimatedSection } from '../animations/AnimatedSection'

type Language = 'javascript' | 'python' | 'curl'

interface CodeExample {
  language: Language
  code: string
  lineCount: number
}

const CODE_EXAMPLES: Record<Language, CodeExample> = {
  javascript: {
    language: 'javascript',
    code: `const response = await fetch('https://api.softitect.com/api/v1/client/chat/converse', {
  method: 'POST',
  headers: { 'X-API-Key': 'your-api-key' },
  body: JSON.stringify({
    modelId: 'claude-3-haiku',
    systemPrompt: 'You are a helpful assistant.',
    messages: [
      { role: 'user', content: 'Explain quantum computing' }
    ]
  })
});
const data = await response.json();`,
    lineCount: 11
  },
  python: {
    language: 'python',
    code: `import requests

response = requests.post(
    'https://api.softitect.com/api/v1/client/chat/converse',
    headers={'X-API-Key': 'your-api-key'},
    json={
        'modelId': 'claude-3-haiku',
        'systemPrompt': 'You are a helpful assistant.',
        'messages': [
            {'role': 'user', 'content': 'Explain quantum computing'}
        ]
    }
)
data = response.json()`,
    lineCount: 13
  },
  curl: {
    language: 'curl',
    code: `curl -X POST https://api.softitect.com/api/v1/client/chat/converse \\
  -H "X-API-Key: your-api-key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "modelId": "claude-3-haiku",
    "systemPrompt": "You are a helpful assistant.",
    "messages": [
      { "role": "user", "content": "Explain quantum computing" }
    ]
  }'`,
    lineCount: 9
  }
}

export const CodeDemoSection = memo(function CodeDemoSection() {
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('javascript')
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(CODE_EXAMPLES[selectedLanguage].code)
      setCopied(true)
      toast.success('Code copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toast.error('Failed to copy code')
      console.error('Copy failed:', error)
    }
  }

  const currentExample = CODE_EXAMPLES[selectedLanguage]

  return (
    <section className="section-spacing bg-[#0F1218] px-4 sm:px-6" aria-labelledby="code-demo-heading">
      <div className="container-custom">
        <AnimatedSection animation="fade-up">
          <div className="text-center mb-8 sm:mb-12">
            <h2 id="code-demo-heading" className="text-3xl sm:text-4xl font-bold text-text mb-3 sm:mb-4">
              Integrate in minutes,{' '}
              <span className="text-gradient">optimize forever</span>
            </h2>
            <p className="text-base sm:text-lg text-text-muted max-w-2xl mx-auto px-4">
              Drop in a few lines of code. We handle model selection, cost optimization, and everything else behind the scenes - now and as new features ship.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Language Switcher */}
            <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Code language selection">
              {(Object.keys(CODE_EXAMPLES) as Language[]).map((lang) => (
                <button
                  key={lang}
                  onClick={() => setSelectedLanguage(lang)}
                  role="tab"
                  aria-selected={selectedLanguage === lang}
                  aria-controls="code-example"
                  className={`px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-medium transition-all min-h-[44px] ${
                    selectedLanguage === lang
                      ? 'bg-[#7C8CFF] text-white'
                      : 'bg-[#0B0D12] text-text-muted hover:text-text border border-[#1A1F2B]'
                  }`}
                >
                  {lang === 'javascript' ? 'JavaScript' : lang === 'python' ? 'Python' : 'cURL'}
                </button>
              ))}
            </div>

            {/* Code Block */}
            <div className="relative card-enhanced" role="region" aria-label="Code example">
              <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-[#1A1F2B]">
                <span className="text-xs sm:text-sm text-text-muted font-mono">
                  {currentExample.language}
                </span>
                <button
                  onClick={handleCopy}
                  className="btn btn-ghost btn-sm flex items-center gap-2 min-h-[44px]"
                  aria-label={copied ? 'Code copied to clipboard' : 'Copy code to clipboard'}
                >
                  {copied ? (
                    <>
                      <Check size={16} aria-hidden="true" />
                      <span className="hidden sm:inline">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={16} aria-hidden="true" />
                      <span className="hidden sm:inline">Copy</span>
                    </>
                  )}
                </button>
              </div>
              
              <div id="code-example" className="p-3 sm:p-4 overflow-x-auto custom-scrollbar" role="tabpanel">
                <pre className="text-xs sm:text-sm lg:text-base">
                  <code className="font-mono text-text">
                    {currentExample.code}
                  </code>
                </pre>
              </div>
            </div>

            <p className="text-center text-sm sm:text-base text-text-muted mt-4 sm:mt-6 px-4">
              Complete request-response flow in just {currentExample.lineCount} lines of code
            </p>
          </div>
        </AnimatedSection>
      </div>
    </section>
  )
})
